import { Injectable, signal, computed, effect, inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, shareReplay, tap } from 'rxjs/operators';
import { StoreConfig, SectionConfig } from '../models/store-config.model';
import { environment } from '@environments/environment';
import { DEFAULT_ABOUT_TEXT, isWithinDates } from '@core/utils/section-settings';

const RESERVED_ROUTES = [
  'auth',
  'productos',
  'categorias',
  'buscar',
  'carrito',
  'checkout',
  'login',
  'registro',
  'favoritos',
  'cuenta',
  'api',
  'assets',
];

const FAISEN_FAVICON = 'favicon.ico';
/** Última configuración vista de cada tienda, para pintarla al recargar sin esperar a la red. */
const STORE_CONFIG_KEY = 'store_config_v1:';

/** Plantilla sin tienda: lo que se ve antes de cargar una y fuera de ellas (directorio, cuenta). */
const DEFAULT_CONFIG: StoreConfig = {
  businessId: 'biz-default',
  branding: {
    siteName: 'FAISEN Store',
    primaryColor: '#0058FC', // azul FAISEN
    secondaryColor: '#FA6400', // naranja FAISEN
    accentColor: '#FA4F00', // naranja intenso FAISEN
  },
  // Sin secciones de ejemplo: Inicio muestra solo lo que la tienda configuró en el admin.
  sections: [],
};

@Injectable({
  providedIn: 'root',
})
export class StoreConfigService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private title = inject(Title);
  private meta = inject(Meta);
  private document = inject(DOCUMENT);

  /**
   * `unavailable`: el slug no existe o la tienda está deshabilitada (404 de store-gtw).
   * `error`: no se pudo consultar (gateway caído, red). En ambos casos el layout no
   * pinta la tienda demo de `config`, que antes aparecía como si fuera la real.
   */
  readonly status = signal<'loading' | 'ready' | 'unavailable' | 'error'>('loading');
  private loadedSlug: string | null = null;

  readonly config = signal<StoreConfig>(DEFAULT_CONFIG);

  /** Activas y dentro de su rango "Visible desde/hasta" configurado en el admin. */
  readonly enabledSections = computed(() => {
    return [...this.config().sections]
      .filter((s) => s.enabled && isWithinDates(s.content?.['settings']?.startDate, s.content?.['settings']?.endDate))
      .sort((a, b) => a.order - b.order);
  });

  readonly branding = computed(() => this.config().branding);

  /** Filtros y datos de producto que el negocio decidió mostrar (lo no configurado se muestra). */
  readonly catalog = computed(() => {
    const c = this.config().catalog;
    return {
      filterType: c?.filters?.type !== false,
      filterCategory: c?.filters?.category !== false,
      filterBrand: c?.filters?.brand !== false,
      filterPrice: c?.filters?.price !== false,
      filterStock: c?.filters?.stock !== false,
      filterSort: c?.filters?.sort !== false,
      showBrand: c?.product?.showBrand !== false,
      showCategory: c?.product?.showCategory !== false,
    };
  });
  readonly currentSlug = signal<string>(this.detectSlug());

  constructor() {
    effect(() => {
      this.applyTheme(this.branding());
    });

    // Pestaña, favicon y metadatos con los datos del negocio (antes: "ShopNow" fijo).
    effect(() => {
      const status = this.status();
      if (status === 'unavailable') {
        this.title.setTitle('Tienda no disponible');
        return;
      }
      if (status !== 'ready') return;
      this.applyIdentity(this.branding(), this.config().aboutText);
    });

    const initialSlug = this.currentSlug();
    if (initialSlug) {
      this.loadStoreConfig(initialSlug).subscribe();
    }

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const detected = this.detectSlug();
      if (detected) {
        this.enterStore(detected);
      } else if (this.isBrowser && this.currentSlug()) {
        // Salió de la tienda (directorio, cuenta): sin esto su tema y datos se quedaban puestos.
        this.leaveStore();
      }
    });
  }

  /** Fija la tienda actual y la carga. MainLayout la llama con el :slug de la ruta (también en SSR). */
  enterStore(slug: string): void {
    if (!slug) return;
    if (slug !== this.currentSlug()) this.currentSlug.set(slug);
    this.loadStoreConfig(slug).subscribe();
  }

  /**
   * Configuración de cada tienda ya cargada en esta visita. Al ir a la cuenta o al login
   * (plantilla FAISEN) y volver, se aplica al instante: antes se volvía a pedir y la
   * tienda se veía con los colores FAISEN hasta que llegaba la respuesta.
   * ponytail: dura lo que la pestaña; un cambio hecho en el admin se ve al recargar.
   */
  private configCache = new Map<string, StoreConfig>();

  // ponytail: localStorage por tienda (una entrada por slug visitado); sin límite porque
  // un cliente visita pocas tiendas. Si crece, guardar solo las últimas N.
  private readPersisted(slug: string): StoreConfig | null {
    if (!this.isBrowser) return null;
    try {
      const raw = localStorage.getItem(STORE_CONFIG_KEY + slug);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private persist(slug: string, config: StoreConfig): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(STORE_CONFIG_KEY + slug, JSON.stringify(config));
    } catch {
      // Sin espacio o almacenamiento bloqueado: la tienda carga igual desde la red.
    }
  }

  private removePersisted(slug: string): void {
    try {
      localStorage.removeItem(STORE_CONFIG_KEY + slug);
    } catch {}
  }

  private leaveStore(): void {
    this.currentSlug.set('');
    this.loadedSlug = null;
    this.inflight = null;
    this.status.set('loading');
    this.config.set(DEFAULT_CONFIG);
    // Fuera de las tiendas (directorio, cuenta) la pestaña vuelve a ser de FAISEN.
    this.title.setTitle('FAISEN Tiendas');
    this.setFavicon(FAISEN_FAVICON);
  }

  /**
   * Tienda de la URL actual, o '' si no se está en una. Sin fallback: a una tienda sólo
   * se llega desde el listado de tiendas activas (/:slug) o por su subdominio.
   */
  detectSlug(): string {
    if (typeof window === 'undefined') return '';

    // 1. Subdominio (ej: mi-negocio.faisen.tech)
    const host = window.location.hostname;
    if (host.includes('.faisen.tech') || host.includes('.localhost')) {
      const sub = host.split('.')[0];
      const reservedSubs = ['www', 'tienda', 'localhost', 'dev', 'stage', 'api', 'admin', 'store'];
      if (sub && !reservedSubs.includes(sub)) return sub;
    }

    // 2. Primer o segundo segmento de ruta (ej: /mi-tienda o /tienda/mi-tienda)
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      if (pathParts[0].toLowerCase() === 'tienda' && pathParts[1]) {
        return pathParts[1].toLowerCase();
      }
      const firstSegment = pathParts[0].toLowerCase();
      if (!RESERVED_ROUTES.includes(firstSegment)) {
        return firstSegment;
      }
    }

    return '';
  }

  /** Petición en curso por slug: constructor, layout y navegación la pedían a la vez (3 GET). */
  private inflight: { slug: string; req: Observable<any> } | null = null;

  /**
   * Carga la tienda una vez por slug. Recargar en cada navegación no aportaba nada y
   * cada respuesta creaba secciones nuevas, lo que cancelaba y repetía los pedidos de
   * productos de Inicio. `force` para "Reintentar".
   */
  loadStoreConfig(slug?: string, force = false): Observable<any> {
    const targetSlug = slug || this.currentSlug();
    // Sin tienda no hay nada que cargar: GET /store/ es el listado, no una configuración.
    if (!targetSlug) return of(this.config());
    if (this.inflight?.slug === targetSlug) return this.inflight.req;
    const cached = this.configCache.get(targetSlug);
    if (!force && cached && targetSlug !== this.loadedSlug) {
      this.config.set(cached);
      this.loadedSlug = targetSlug;
      this.status.set('ready');
    }
    if (!force && targetSlug === this.loadedSlug && this.status() === 'ready') return of(this.config());
    // Primera vez en esta pestaña (recarga, enlace directo): se pinta la última versión
    // guardada al instante y la petición de abajo la actualiza sin pasar por "cargando".
    if (!force && targetSlug !== this.loadedSlug) {
      const persisted = this.readPersisted(targetSlug);
      if (persisted) {
        this.config.set(persisted);
        this.loadedSlug = targetSlug;
        this.status.set('ready');
      }
    }
    const req = this.fetchStoreConfig(targetSlug).pipe(
      finalize(() => {
        if (this.inflight?.slug === targetSlug) this.inflight = null;
      }),
      shareReplay(1),
    );
    this.inflight = { slug: targetSlug, req };
    return req;
  }

  private fetchStoreConfig(targetSlug: string): Observable<any> {
    // Se recarga en cada navegación: solo mostrar "cargando" al cambiar de tienda.
    if (this.isBrowser && targetSlug !== this.loadedSlug) this.status.set('loading');
    return this.http.get<any>(`${environment.apiUrl}/store/${targetSlug}`).pipe(
      tap((res) => {
        this.loadedSlug = targetSlug;
        if (this.isBrowser) this.status.set('ready');
        if (res) {
          // Desde la plantilla vacía: mezclar sobre la tienda anterior le dejaba sus secciones.
          this.config.set(DEFAULT_CONFIG);
          this.updateConfig({
            businessId: res.businessId,
            branding: {
              siteName: res.branding?.siteName || res.businessName || 'FAISEN Store',
              logoUrl: res.branding?.logoUrl || res.logoUrl,
              heroBannerUrl: res.branding?.heroBannerUrl,
              primaryColor: res.branding?.primaryColor || '#0058FC',
              secondaryColor: res.branding?.secondaryColor || '#FA6400',
              accentColor: res.branding?.accentColor || '#FA4F00',
            },
            whatsappNumber: res.whatsappNumber,
            address: res.address,
            aboutText: res.aboutText,
            instagramUrl: res.instagramUrl,
            facebookUrl: res.facebookUrl,
            tiktokUrl: res.tiktokUrl,
            sections: res.sections && res.sections.length > 0 ? res.sections : undefined,
            catalog: res.catalog,
          });
          this.configCache.set(targetSlug, this.config());
          this.persist(targetSlug, this.config());
        }
      }),
      catchError((err: HttpErrorResponse) => {
        // ponytail: el estado solo se decide en el navegador; en SSR el servidor pinta la
        // tienda o la plantilla, y el navegador muestra "no disponible"/"error" al hidratar.
        if (this.isBrowser) {
          this.loadedSlug = targetSlug;
          this.status.set(err?.status === 404 ? 'unavailable' : 'error');
          // Tienda que ya no existe: no volver a pintarla desde lo guardado.
          if (err?.status === 404) this.removePersisted(targetSlug);
        }
        return of(this.config());
      })
    );
  }

  /** Nombre, descripción (Sobre Nosotros) y logo del negocio en la pestaña y al compartir el link. */
  private applyIdentity(branding: StoreConfig['branding'], aboutText: string | undefined) {
    const name = branding.siteName || 'FAISEN Tiendas';
    const description = aboutText || DEFAULT_ABOUT_TEXT;
    this.title.setTitle(name);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: name });
    this.meta.updateTag({ property: 'og:description', content: description });
    if (branding.logoUrl) this.meta.updateTag({ property: 'og:image', content: branding.logoUrl });
    // Sin logo, el de FAISEN: si no, quedaba el ícono de la tienda visitada antes.
    this.setFavicon(branding.logoUrl || FAISEN_FAVICON);
  }

  private setFavicon(href: string): void {
    const icon = this.document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon) icon.href = href;
  }

  applyTheme(branding: StoreConfig['branding']) {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', branding.primaryColor);
    root.style.setProperty('--brand-secondary', branding.secondaryColor);
    root.style.setProperty('--brand-accent', branding.accentColor);
  }

  updateConfig(newConfig: Partial<StoreConfig>) {
    this.config.update((curr) => ({
      ...curr,
      ...newConfig,
      branding: { ...curr.branding, ...(newConfig.branding || {}) },
      sections: newConfig.sections && newConfig.sections.length > 0 ? newConfig.sections : curr.sections,
    }));
  }

  updateSectionOrder(sections: SectionConfig[]) {
    this.config.update((curr) => ({
      ...curr,
      sections,
    }));
  }
}
